import React from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import moment from "moment";

import ReportPaymentsInvoices from "../../../validations/ReportPaymentsInvoices";
import { ReportOperatorSalary } from "../../../models/ReportOperatorSalary";

import axiosClient from "../../../config/axios";

import OrdersPaymentsInvoicesForm from "../../../forms/ordersPaymentsInvoicesForm";
import TableOrderInvoicePayment from "../../../components/reports/TableOrderInvoicePayment";
import Loader from "../../../components/loader";

const OrdersPaymentsInvoices = () => {
    const [loading, setLoading] = React.useState(true);
    const [records, setRecords] = React.useState([]);
    const [orders, setOrders] = React.useState([]);
    const [status, setStatus] = React.useState([]);
    const [paymentTypes, setPaymentTypes] = React.useState([]);
    const [vehicleTypes, setVehicleTypes] = React.useState([]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            ...ReportOperatorSalary(),
            paymentTypes: "all",
            companyType: "Gruas Varo", 
            paymentStart: "",
            paymentEnd: "",
            invoiceStart: "",
            invoiceEnd: "",
            vehicleTypeId: "all", 
        },
        validationSchema: ReportPaymentsInvoices,

       onSubmit: async (values) => {
        setLoading(true);
        try {
            const adjustedStart = moment(values.start).format("YYYY-MM-DD");
            const adjustedEnd = moment(values.end).format("YYYY-MM-DD");

            const paymentStartParam = values.paymentStart
                ? `&paymentStart=${moment(values.paymentStart).format("YYYY-MM-DD")}`
                : "";
            const paymentEndParam = values.paymentEnd
                ? `&paymentEnd=${moment(values.paymentEnd).format("YYYY-MM-DD")}`
                : "";
            const invoiceStartParam = values.invoiceStart
            ? `&invoiceStart=${moment(values.invoiceStart).format("YYYY-MM-DD")}` : "";

            const invoiceEndParam = values.invoiceEnd
            ? `&invoiceEnd=${moment(values.invoiceEnd).format("YYYY-MM-DD")}` : "";

            const vehicleTypeParam = values.vehicleTypeId && values.vehicleTypeId !== "all" 
                ? `&vehicleTypeId=${values.vehicleTypeId}` 
                : "";

            // Obtener pagos e invoices
            const endpoint = `/reports/payments-invoices?start=${values.start}&end=${values.end}&status=${values.status}&PaymentType=${values.paymentTypes}${paymentStartParam}${paymentEndParam}${invoiceStartParam}${invoiceEndParam}${vehicleTypeParam}`;
            //const endpoint = `/reports/payments-invoices?start=${values.start}&end=${values.end}&status=${values.status}&PaymentType=${values.paymentTypes}`;
            const { data } = await axiosClient.get(endpoint);

            const dataTable = (data?.orders || []).map((order) => ({
            ...order,
            tipo: "Gruas Varo",
            tipoOrden: "ordenNormal",
            razon: "Gruas Varo",
            subtotal: order.orden_en_cero ? 0 : order.subtotal 
            }));

            // Unir las órdenes normales modificadas con las viales transformadas
            const allOrders = [
            ...(data?.orders || []).map(order => ({
                ...order,
                tipo: "Gruas Varo",
                tipoOrden: "ordenNormal",
                razon: "Gruas Varo",
                subtotal: order.orden_en_cero ? 0 : order.subtotal
            })),
            ];

            // Filtrar por tipo si no es "all"
            const filteredDataTable = values.companyType === "all"
            ? dataTable
            : dataTable.filter(order => order.tipo === values.companyType);

            const filteredAllOrders = values.companyType === "all"
            ? allOrders
            : allOrders.filter(order => order.tipo === values.companyType);

            // Ordenar justo antes de hacer el set
            filteredDataTable.sort((a, b) => new Date(a.fecha_hora_reporte) - new Date(b.fecha_hora_reporte));
            filteredAllOrders.sort((a, b) => new Date(a.fecha_hora_reporte) - new Date(b.fecha_hora_reporte));

            //console.log("datos tabla", filteredDataTable)
            setRecords(filteredAllOrders);
            setOrders(filteredDataTable);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
        },
    });

    const fetchData = async () => {
        try {
            // Fetch statuses
            const responseStatus = await axiosClient.get('/orderStatuses/allWithOrderCount');
            const formattedOptionsStatus = [
                { value: "all", label: "Todas las opciones" },
                ...responseStatus.data.data.map(operator => ({
                    label: operator.order_status,
                    value: operator.order_status
                }))
            ];
            setStatus(formattedOptionsStatus);

            // Vehicle types
            const responseVehicleTypes = await axiosClient.get("/vehicleTypes/all");

            const uniqueTypes = [];
            const seen = new Set();

            responseVehicleTypes.data.data.forEach((vt) => {
            // Normaliza para evitar diferencias de acentos/mayúsculas
            const normalized = vt.vehicle_type_name
                .normalize("NFD") // separa letras y acentos
                .replace(/[\u0300-\u036f]/g, "") // elimina acentos
                .toUpperCase();

            if (!seen.has(normalized)) {
                seen.add(normalized);
                uniqueTypes.push({
                label: vt.vehicle_type_name,
                value: vt.vehicle_type_name,
                });
            }
            });

            const formattedVehicleTypes = [
            { value: "all", label: "TODOS" },
            ...uniqueTypes,
            ];

            setVehicleTypes(formattedVehicleTypes);

            // Fetch payment types
            const responsePaymentTypes = await axiosClient.get('/paymentTypes/all');
            const formattedPaymentTypes = [
                { value: "all", label: "TODOS" },
                ...responsePaymentTypes.data.data.map(pt => ({
                    label: pt.payment_type_name,
                    value: pt.payment_type_code
                }))
            ];

            setPaymentTypes(formattedPaymentTypes);

        } catch (error) {
            console.error(error);
        }
    };

    React.useEffect(() => {
        formik.handleSubmit();
        fetchData();
    }, []);

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Inicio</Link>
                        </li>
                        <li className="breadcrumb-item active">Reporte de pagos y facturas</li>
                    </ol>
                    <h1 className="page-header mb-0">Reporte de pagos y facturas</h1>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-xl-12 card border-0 p-3">
                    <OrdersPaymentsInvoicesForm formik={formik} records={records} status={status} paymentTypes={paymentTypes} vehicleTypes={vehicleTypes}/>
                </div>
            </div>
            <div className="row">
                <div className="col-xl-12 card border-0 p-3">
                    <TableOrderInvoicePayment
                        orders={orders}
                        start={formik.values.start}
                        end={formik.values.end}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default OrdersPaymentsInvoices;

